import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { boardStateAtom, presentationBoundsAtom, presentationElementIdAtom, presentationModeAtom, presentationPositionAtom, presentationViewChangedAtom, presentationZoomAtom, } from '@/store/boardAtoms';
import { getElementBounds, sortPresentationElements } from '@/utils/presentation';
import { useResetPresentationState } from './useResetPresentationState';
/**
 * Кастомный хук для управления режимом презентации
 *
 * @param params - Параметры хука
 * @returns Объект с функциями управления презентацией
 */
export const usePresentationMode = ({ role, send }) => {
    const [boardState] = useAtom(boardStateAtom);
    const [presentationMode, setPresentationMode] = useAtom(presentationModeAtom);
    const [presentationElementId, setPresentationElementId] = useAtom(presentationElementIdAtom);
    const [presentationBounds, setPresentationBounds] = useAtom(presentationBoundsAtom);
    const [presentationZoom, setPresentationZoom] = useAtom(presentationZoomAtom);
    const [presentationPosition, setPresentationPosition] = useAtom(presentationPositionAtom);
    const [presentationViewChanged, setPresentationViewChanged] = useAtom(presentationViewChangedAtom);
    // Хук для сброса состояния презентации
    const resetPresentationState = useResetPresentationState();
    /** Начать режим презентации */
    const startPresentation = useCallback(() => {
        // Собираем элементы презентации перед каждым стартом, чтобы учесть последние изменения
        const initialPresentationElements = [];
        for (const layer of boardState.layers || []) {
            for (const element of layer.elements || []) {
                if (element.isPresentationFrame) {
                    initialPresentationElements.push(element);
                }
            }
        }
        const sorted = sortPresentationElements(initialPresentationElements);
        const active = sorted.find((el) => el.isActivePresentationFrame);
        const element = active !== null && active !== void 0 ? active : (sorted.length > 0 ? sorted[0] : null);
        if (!element)
            return;
        // Вычисляем границы элемента
        const bounds = getElementBounds(element);
        setPresentationElementId(element.id);
        setPresentationBounds(bounds);
        setPresentationMode(true);
        // Устанавливаем начальную позицию в (0, 0) - центрирование будет сделано в PresentationMode
        resetPresentationState();
        if (send) {
            // Сохраняем состояние презентации на сервере
            send({
                type: 'board.settings.update',
                payload: {
                    isPresentationMode: true,
                },
            });
        }
    }, [
        setPresentationElementId,
        setPresentationBounds,
        setPresentationMode,
        resetPresentationState,
        send,
        boardState.layers,
    ]);
    /**
     * Завершить режим презентации
     */
    const stopPresentation = useCallback(() => {
        setPresentationMode(false);
        setPresentationElementId(null);
        setPresentationBounds(null);
        resetPresentationState();
        if (send) {
            // Сохраняем состояние презентации на сервере
            send({
                type: 'board.settings.update',
                payload: {
                    isPresentationMode: false,
                },
            });
        }
    }, [
        setPresentationMode,
        setPresentationElementId,
        setPresentationBounds,
        resetPresentationState,
        send,
    ]);
    /**
     * Сбросить позицию и зум презентации к начальному состоянию
     */
    const resetPresentationView = useCallback(() => {
        if (!presentationMode)
            return;
        setPresentationZoom(1);
        setPresentationPosition({ x: 0, y: 0 });
        setPresentationViewChanged(false);
    }, [presentationMode, setPresentationZoom, setPresentationPosition, setPresentationViewChanged]);
    return {
        presentationMode,
        presentationElementId,
        presentationBounds,
        presentationZoom,
        presentationPosition,
        presentationViewChanged,
        startPresentation,
        stopPresentation,
        resetPresentationView,
        canStartPresentation: role === 'GM',
        canStopPresentation: role === 'GM',
    };
};
